import { cn } from '@/lib/utils';

export function formatModCount(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat().format(value);
}

/** Prior value implied by current + percent delta (for 2-point trend UI). */
export function priorFromDelta(value: number, deltaPercent: number | null | undefined): number | null {
  if (deltaPercent == null || !Number.isFinite(deltaPercent)) return null;
  const factor = 1 + deltaPercent / 100;
  if (Math.abs(factor) < 1e-6) return null;
  return value / factor;
}

/**
 * Deterministic decorative sparkline from current value + delta.
 * Prefer API `sparkline` series when available — this is a last-resort fallback only.
 */
export function sparkPointsFromDelta(
  value: number | null | undefined,
  deltaPercent: number | null | undefined,
  points = 8,
): number[] {
  if (value == null || !Number.isFinite(value)) return [];
  const prior = priorFromDelta(value, deltaPercent) ?? value * 0.92;
  const out: number[] = [];
  for (let i = 0; i < points; i++) {
    const t = i / (points - 1);
    const base = prior + (value - prior) * t;
    const wobble = Math.sin(i * 1.7 + value * 0.01) * Math.abs(value - prior) * 0.08;
    out.push(Math.max(0, base + wobble));
  }
  return out;
}

export function MiniSparkline({
  values,
  className,
  stroke = '#60a5fa',
  fill,
  showDots = false,
}: {
  values: number[];
  className?: string;
  stroke?: string;
  fill?: string;
  /** Figma area charts draw point markers on the polyline. */
  showDots?: boolean;
}) {
  if (values.length < 2) {
    return <div className={cn('h-8 w-16', className)} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 120;
  const h = 36;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / span) * (h - 8) - 4;
    return { x, y };
  });
  const line = points.map((p) => `${p.x},${p.y}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={cn('h-9 w-full max-w-[9rem] shrink-0', className)}
      preserveAspectRatio={showDots ? 'xMidYMid meet' : 'none'}
      aria-hidden
    >
      {fill ? <polyline points={area} fill={fill} stroke="none" /> : null}
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {showDots
        ? points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="2.25" fill={stroke} />
          ))
        : null}
    </svg>
  );
}

/** Vertical bar sparkline used on Platform Safety Health rate rows (Figma). */
export function MiniBarSparkline({
  values,
  className,
  fill = '#a78bfa',
}: {
  values: number[];
  className?: string;
  fill?: string;
}) {
  if (values.length < 2) {
    return <div className={cn('h-8 w-16', className)} />;
  }
  const max = Math.max(...values, 1);
  const w = 120;
  const h = 36;
  const gap = 3;
  const barW = Math.max(3, (w - gap * (values.length - 1)) / values.length);

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={cn('h-9 w-full max-w-[9rem] shrink-0', className)}
      preserveAspectRatio="none"
      aria-hidden
    >
      {values.map((v, i) => {
        const barH = Math.max(3, (v / max) * (h - 2));
        const x = i * (barW + gap);
        return (
          <rect
            key={i}
            x={x}
            y={h - barH}
            width={barW}
            height={barH}
            rx={2}
            fill={fill}
            opacity={0.5 + (i / Math.max(1, values.length - 1)) * 0.5}
          />
        );
      })}
    </svg>
  );
}

export function ConicDonut({
  segments,
  centerValue,
  centerLabel,
  sizeClass = 'size-36',
}: {
  segments: { label: string; value: number; color: string }[];
  centerValue: string;
  centerLabel?: string;
  sizeClass?: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  let cursor = 0;
  const stops = segments
    .map((s) => {
      const start = (cursor / total) * 100;
      cursor += s.value;
      return `${s.color} ${start}% ${(cursor / total) * 100}%`;
    })
    .join(', ');

  return (
    <div
      className={cn('relative shrink-0 rounded-full p-[14px]', sizeClass)}
      style={{ background: `conic-gradient(${stops})` }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-[#111114]">
        <span className="text-lg font-semibold tabular-nums text-white">{centerValue}</span>
        {centerLabel ? (
          <span className="text-[10px] text-muted-foreground">{centerLabel}</span>
        ) : null}
      </div>
    </div>
  );
}
